import type { ColumnsType } from "antd/es/table";
import { PartnerDTO } from "../../types/partner/PartnerDTO";
import PartnerActionButton from "./action/PartnerActionButton";
import { Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useSearchColumn } from "./useSearchColumn";
import {
  useGetCategoriesQuery,
  useGetCitiesQuery,
  useGetEmployeeQuery,
} from "../../store/api/CategoryApi";
import { useTableSearchParams } from "../../hooks/UseTableSearchParams";
import { isPickup } from "../../types/order/OrderTypeDTO";

type UseColumnsProps = {
  showMap: (
    latitude: number | undefined,
    longitude: number | undefined
  ) => void;
};

type UseColumnsType = ({ showMap }: UseColumnsProps) => ColumnsType<PartnerDTO>;

export const useColumns: UseColumnsType = ({ showMap }) => {
  const { getColumnSearchProps } = useSearchColumn();
  const { filter } = useTableSearchParams();

  const { data } = useGetCategoriesQuery({
    page: filter.page,
    size: filter.size,
  });
  const { data: employees } = useGetEmployeeQuery({
    page: filter.page,
    size: filter.size,
  });
  const { data: cities } = useGetCitiesQuery({
    page: filter.page,
    size: filter.size,
  });

  const columns: ColumnsType<PartnerDTO> = [
    {
      title: "Название заведения",
      dataIndex: "name",
      key: "name",
      filteredValue: filter.partner_name ? [filter.partner_name] : null,
      filterIcon: () => <SearchOutlined />,
      render: (_, record) => (
        <div
          title={record.id}
          onClick={() => navigator.clipboard.writeText(record.id)}
        >
          {record.name}
        </div>
      ),
      ...getColumnSearchProps("Введите название заведения", "text"),
    },
    {
      title: "Города",
      dataIndex: "address",
      key: "address",
      filterIcon: () => <SearchOutlined />,
      render: (_, { address }) => address.street.city,
      filteredValue: filter.city_ids ? filter.city_ids : null,
      filters: cities?.data?.map((val) => ({
        text: val.name,
        value: val.id,
      })),
    },
    {
      title: "Адрес",
      dataIndex: "address",
      key: "street",
      render: (_, { address, service_type }) =>
        isPickup(service_type)
          ? address.branch?.name ?? "—"
          : address.street.name ?? "—",
    },

    {
      title: "Локация",
      dataIndex: "map",
      key: "map",
      render: (_, { address }) =>
        address.latitude != null && address.longitude != null ? (
          <Button
            onClick={() =>
              showMap(Number(address.latitude), Number(address.longitude))
            }
          >
            Посмотреть на карте
          </Button>
        ) : (
          "—"
        ),
    },
    {
      title: "Тип сервиса",
      dataIndex: "service_type",
      key: "service_type",
      render: (_, { service_type }) => {
        const v = String(service_type ?? "").toUpperCase();
        if (v === "DELIVERYBYCLIENT") return "Самовывоз";
        if (v === "DELIVERYBYCOURIER") return "Доставка";
        return "—";
      },
    },
    {
      title: "Купон",
      dataIndex: "coupon",
      key: "coupon",
      filteredValue: filter.coupon_name ? [filter.coupon_name] : null,
      filterIcon: () => <SearchOutlined />,
      render: (_, record) => `${record.coupon_name} ${record.coupon_percent}%`,
      ...getColumnSearchProps("Введите название купона ", "text"),
    },
    {
      title: "Категория заведения",
      dataIndex: "category",
      key: "category",
      filterIcon: () => <SearchOutlined />,
      render: (_, { category }) => category.name,
      filteredValue: filter.category_ids ? filter.category_ids : null,
      filters: data?.data?.content.map((val) => ({
        text: val.name,
        value: val.id,
      })),
    },
    {
      title: "Добавил",
      dataIndex: "created_by",
      key: "created_by",
      filterIcon: () => <SearchOutlined />,
      render: (_, { created_by }) => created_by.name,
      filteredValue: filter.employee_ids ? filter.employee_ids : null,
      filters: employees?.data?.map((val) => ({
        text: val.name,
        value: val.id,
      })),
    },

    {
      title: "Действие",
      dataIndex: "action",
      key: "action",
      align: "center",
      render: (_, record) => <PartnerActionButton partner={record} />,
    },
  ];

  return columns;
};
